import type { BootstrapDocument, BootstrapRelay, RelayTier } from "@veto/protocol";

/** Lab topology — local docker-compose (`infra/docker-compose.yml`). */
const LAB_RELAYS: BootstrapRelay[] = [
  { url: "ws://127.0.0.1:7071", provider: "A", region: "eu-west", auth: "dev", tier: "primary" },
  { url: "ws://127.0.0.1:7072", provider: "B", region: "us-east", auth: "dev", tier: "primary" },
  { url: "ws://127.0.0.1:7073", provider: "A", region: "ap-southeast", auth: "dev", tier: "primary" },
  { url: "ws://127.0.0.1:7074", provider: "C", region: "eu-west", auth: "dev", tier: "fallback" },
  { url: "ws://127.0.0.1:7075", provider: "C", region: "us-east", auth: "dev", tier: "fallback" },
];

const LAB_TURN: BootstrapDocument["turn"] = [
  { url: "turn:127.0.0.1:3478", credential_endpoint: "http://127.0.0.1:7091/turn" },
  { url: "turn:127.0.0.1:3479", credential_endpoint: "http://127.0.0.1:7091/turn" },
];

/**
 * Hosted fallback relays — stable private Fly.io targets (services/relay-dev/fly.toml).
 * Used when `VITE_RELAY_URLS` is unset on a production build so a deployed PWA is
 * never left with zero relays (NET-REL-003 approved-route expansion).
 */
const HOSTED_FALLBACK_RELAYS: BootstrapRelay[] = [
  { url: "wss://veto.martialtech.fr/relay", provider: "A", region: "nas", auth: "dev", tier: "fallback" },
];

/**
 * Community (public Nostr) relays — NET-REL-006. Different privacy boundary
 * (operators see the recipient routing tag, timing and bucketed size; content and
 * sender stay sealed) and shown amber in the UI. With protocol-v2 sealed
 * envelopes they carry no social graph, so they are part of the default pool:
 * a messenger must still deliver when every operated relay is down.
 *
 * Curated from a read-only survey (NIP-11 + #w/#p REQ, 2026-09-26): free, no
 * payment/web-of-trust write restrictions, diverse operators and relay software
 * (strfry, nostr-rs-relay, memlay, custom). relay.damus.io and relay.nostr.net
 * gate kind-1059 reads behind NIP-42 — served via AUTH with the inbox key.
 */
export const COMMUNITY_RELAY_URLS: readonly string[] = [
  "wss://nos.lol",
  "wss://relay.damus.io",
  "wss://relay.primal.net",
  "wss://nostr.mom",
  "wss://offchain.pub",
  "wss://relay.snort.social",
  "wss://nostr.oxtr.dev",
  "wss://relay.nos.social",
  "wss://nostr.bitcoiner.social",
  "wss://nostr-pub.wellorder.net",
  "wss://relay.nostr.net",
  "wss://nostr.data.haus",
  "wss://yabu.me",
  "wss://nostr.stakey.net",
  "wss://relay.nostr.wirednet.jp",
  "wss://nostrue.com",
];

const COMMUNITY_RELAYS: BootstrapRelay[] = COMMUNITY_RELAY_URLS.map((url) => ({
  url,
  provider: "community",
  region: "global",
  auth: "open",
  tier: "community",
}));

const REGIONS = ["eu-west", "us-east", "ap-southeast"] as const;

function readViteEnv(key: string): string | undefined {
  try {
    const env = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
    const v = env?.[key];
    return typeof v === "string" && v.trim().length > 0 ? v.trim() : undefined;
  } catch {
    return undefined;
  }
}

function isProdClientBuild(): boolean {
  try {
    const env = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
    return env?.PROD === true || env?.MODE === "production";
  } catch {
    return false;
  }
}

/** Comma/space-separated relay WebSocket URLs (prefer `wss://` on HTTPS hosts). */
export function parseRelayUrlList(
  raw: string | undefined,
  tier: RelayTier = "primary",
): BootstrapRelay[] | null {
  if (!raw?.trim()) return null;
  // Vercel "Sensitive" build placeholders must not become fake relay URLs.
  if (/^\[SENSITIVE\]$/i.test(raw.trim())) return null;
  const urls = raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && /^(wss?|https?):\/\//i.test(s));
  if (urls.length === 0) return null;
  return urls.map((url, i) => ({
    url,
    provider: String.fromCharCode(65 + (i % 3)),
    region: REGIONS[i % REGIONS.length]!,
    auth: "dev",
    tier,
  }));
}

function isHttpsPage(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.location?.protocol === "string" &&
      window.location.protocol.toLowerCase() === "https:"
    );
  } catch {
    return false;
  }
}

function dropInsecure(relays: BootstrapRelay[]): BootstrapRelay[] {
  if (!isHttpsPage()) return relays;
  // SEC (SPEC §1.2 minimal metadata / §9.5): on an HTTPS page, plaintext `ws://`
  // relays are dropped — envelope metadata must not traverse the network unencrypted.
  return relays.filter((r) => /^wss:\/\//i.test(r.url));
}

function dedupeRelays(relays: BootstrapRelay[]): BootstrapRelay[] {
  const seen = new Set<string>();
  const out: BootstrapRelay[] = [];
  for (const r of relays) {
    const key = r.url.toLowerCase().replace(/\/+$/, "");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/**
 * Resolve bootstrap relays (NET-REL / INF bootstrap). Layered so a production
 * deployment never ends up with zero routes:
 * 1. `VITE_RELAY_URLS` — primary operated relays.
 * 2. `VITE_FALLBACK_RELAY_URLS` — secondary operated relays (tier: fallback).
 * 3. Production builds without env → hosted fallback targets (Fly.io relays).
 * 4. Dev/test without env → lab `ws://127.0.0.1:7071–7075`.
 */
export function resolveBootstrapRelays(
  envUrls: string | undefined = readViteEnv("VITE_RELAY_URLS"),
  prod: boolean = isProdClientBuild(),
  envFallbackUrls: string | undefined = readViteEnv("VITE_FALLBACK_RELAY_URLS"),
): BootstrapRelay[] {
  const primary = parseRelayUrlList(envUrls, "primary");
  const secondary = parseRelayUrlList(envFallbackUrls, "fallback");
  let list: BootstrapRelay[];
  if (primary || secondary) {
    list = [...(primary ?? []), ...(secondary ?? [])];
  } else if (prod) {
    list = HOSTED_FALLBACK_RELAYS;
  } else {
    list = LAB_RELAYS;
  }
  return dedupeRelays(dropInsecure(list));
}

/** Community relays of the default pool (NET-REL-006, amber tier). */
export function resolveCommunityRelays(): BootstrapRelay[] {
  return dropInsecure(COMMUNITY_RELAYS);
}

/** Supabase Realtime fallback config — enabled only when both env values are set. */
export function resolveSupabaseConfig(): { url: string; anonKey: string } | null {
  const url = readViteEnv("VITE_SUPABASE_URL");
  const anonKey = readViteEnv("VITE_SUPABASE_ANON_KEY");
  if (!url || !anonKey || !/^https?:\/\//i.test(url)) return null;
  return { url: url.replace(/\/+$/, ""), anonKey };
}

/* ------------------------- runtime relay overrides ------------------------ */

/**
 * User-managed relay overrides (Settings → Network). Custom relays merge with
 * the bootstrap list; disabled bootstrap relays are dropped. Session-scoped
 * until saved via netPrefs in the app layer.
 */
let activeOverrides: { custom: BootstrapRelay[]; disabled: Set<string> } = {
  custom: [],
  disabled: new Set(),
};

export function setActiveRelayOverrides(opts: {
  customUrls?: string[];
  disabledUrls?: string[];
}): void {
  activeOverrides = {
    custom: (opts.customUrls ?? []).map((url, i) => ({
      url,
      provider: "custom",
      region: REGIONS[i % REGIONS.length]!,
      auth: "dev",
      tier: "primary" as RelayTier,
    })),
    disabled: new Set((opts.disabledUrls ?? []).map((u) => u.toLowerCase().replace(/\/+$/, ""))),
  };
}

export function getActiveRelayOverrides(): { custom: BootstrapRelay[]; disabled: string[] } {
  return { custom: activeOverrides.custom, disabled: [...activeOverrides.disabled] };
}

/**
 * Effective private relay list = bootstrap ∪ custom − disabled. HTTPS pages drop
 * plaintext ws:// entries and `wss://` is enforced for community tier always.
 */
export function composeEffectiveRelays(
  base: BootstrapRelay[] = DEFAULT_BOOTSTRAP.relays,
): BootstrapRelay[] {
  const merged = dedupeRelays([...activeOverrides.custom, ...base]);
  return dropInsecure(merged).filter((r) => !activeOverrides.disabled.has(normalizeRelayKey(r.url)));
}

export function normalizeRelayKey(url: string): string {
  return url.toLowerCase().replace(/\/+$/, "");
}

export const DEFAULT_BOOTSTRAP: BootstrapDocument = {
  version: 1,
  valid_from: "2026-07-19T00:00:00Z",
  expires_at: "2027-07-19T00:00:00Z",
  min_client_protocol: 1,
  recommended_client_protocol: 1,
  relays: resolveBootstrapRelays(),
  turn: resolveTurn(),
  features: { public_mesh: true, calls: false },
  signature: "dev-unsigned",
};

function resolveTurn(
  envUrls: string | undefined = readViteEnv("VITE_TURN_URLS"),
  envCred: string | undefined = readViteEnv("VITE_TURN_CREDENTIAL_ENDPOINT"),
  prod: boolean = isProdClientBuild(),
): BootstrapDocument["turn"] {
  if (!envUrls?.trim()) return prod ? [] : LAB_TURN;
  const urls = envUrls
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  // SEC: TURN credential endpoints carry short-lived secrets — never fetch
  // them over plaintext http from an HTTPS page (SPEC §10.4 ephemeral REST).
  const cred = envCred?.trim() || "";
  const safeCred = isHttpsPage() && /^http:\/\//i.test(cred) ? "" : cred;
  return urls.map((url) => ({ url, credential_endpoint: safeCred }));
}
